/* eslint-disable @typescript-eslint/no-explicit-any */
import { SingleResponse, Bank, BankAccountStatus, BankAccountType, BankVerifyRequest, CustomerApplicationProduct, DocumentTypeEnum, FileUploadRequest, Resource, SearchParams, TaskRequestType, VendorApplication } from 'common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { mockVendorApplication } from './mockData/mock-data';
import { StaticBankList } from '../utils/constants';
import { Injectable, inject } from '@angular/core';
import { Observable, of, delay, map } from 'rxjs';
import { AuthService } from './auth.service';



/**
 * Shared API service for extension ONLY.
 * Shared APIs are used by both the Customer and Optometrist zones.
 * Specific APIs should be placed in their respective services.
 * 
 * You should not be using this service directly in the Customer or Optometrist zones.
 * When using for shared components within the ng-shared package, it's acceptable to inject this service into that component directly.
 * 
 * @example
 * ```typescript
 * // Usage in the Customer Zone:  
 * @Injectable({ providedIn: 'root' })
 * export class CustomerService extends SharedApiService {
 *   // Your code here.
 * }
 *```

 * @export SharedApiService
 * @class SharedApiService
 * @memberof Services
 */
@Injectable({ providedIn: 'root' })
export abstract class SharedApiService {

    protected useMocks = USE_MOCKS;
    // protected useMocks = false;

    protected get baseUrl() {
        return API_BASE_URL + '/api'
    }

    protected authService: AuthService = inject(AuthService);

    /**
     * The headers to use for authenticated requests in the format that HttpClient expects.
     * If the user is not authenticated, this will throw an error.
     *
     * @readonly
     * @protected
     * @type {AngularLikeHttpHeaders}
     * @memberof SharedApiService
     */
    public get authHeaders(): AngularLikeHttpHeaders {
        return {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': this.authService.bearerToken
            })
        };
    }

    constructor(public http: HttpClient) { }

    //#region Mutually Shared APIs 

    public uploadFile(fileData: FileUploadRequest): Observable<Resource> {
        if (this.useMocks) {
            return this.mockRequest<Resource>(
                {
                    contentUrl: 'https://example.com/some-file-url',
                    contentType: 'content/type',
                    extension: '.mom',
                    id: new Date().getTime(),
                    name: fileData?.fileName || 'Some File',
                    size: 123456,
                });
        } else {
            return this.http.post<Resource>(`${this.baseUrl}/resource/upload`, fileData, this.authHeaders);
        }
    }

    /**
     * API for:
     * - Saved Applications
     * - Submitted Applications
     * 
     * Including any CANCELLED or DECLINED applications.
     * 
     * Api by:
     * - Optometrist Zone
     * 
     * @param params 
     * @returns 
     */
    public searchApplication(params: SearchParams): Observable<SingleResponse<VendorApplication[]>> {
        if (params.pageSize === undefined) params.pageSize = 10;
        if (params.pageNumber === undefined) params.pageNumber = 1;
        if (!params.sortDirection) params.sortDirection = 'desc';
        if (!params.sortBy) params.sortBy = 'id';

        if (this.useMocks) {
            // return this.mockRequest<any>(mockCustomerApplicationsResponse);
            return this.mockRequest<any>(<SingleResponse<VendorApplication[]>>{ result: [], total: 0 });
        } else {
            return this.http.post<SingleResponse<VendorApplication[]>>(`${this.baseUrl}/vendorApplication/search`, params, this.authHeaders)
                .pipe(map(response => {
                    // Response "result" is a string that needs to be parsed.
                    response.result = JSON.parse(response.result as any);
                    return response;
                }));
        }
    }

    /**
     * @deprecated - still under development.
     */
    public getFullVendorEntity(applicationId: number): Observable<SingleResponse<VendorApplication[]>> {
        if (this.useMocks) {
            // return this.mockRequest<any>(mockCustomerApplicationsResponse);
            return this.mockRequest<any>(<SingleResponse<VendorApplication[]>>{ result: [], total: 0 });
        } else {
            return this.http.get<SingleResponse<VendorApplication[]>>(`${this.baseUrl}/vendorApplication/getFullEntity/${applicationId}`, this.authHeaders)
                .pipe(map(response => {
                    // console.log('getFullVendorEntity/getFullEntity > response', response);
                    // Response "result" is a string that needs to be parsed.
                    response.result = JSON.parse(response.result as any);
                    return response;
                }));
        }
    }

    public getFullProductEntity(applicationId: number): Observable<CustomerApplicationProduct> {
        if (this.useMocks) {
            return this.mockRequest<CustomerApplicationProduct>({} as any);
        } else {
            return this.http.get<CustomerApplicationProduct>(`${this.baseUrl}/customerProduct/refreshEntity/${applicationId}`, this.authHeaders);
        }
    }

    /**
     * API for:
     * - Pre-Approved Applications
     * - Active Applications
     * - Cancelled or Declined Applications
     * - Closed Applications
     * - Legal, Deceased, or Administration Applications
     * 
     * Api by:
     * - Optometrist Zone
     * - Customer Zone
     */
    public searchCustomerProduct(params: SearchParams): Observable<SingleResponse<CustomerApplicationProduct[]>> {
        // To find the patients of the  
        // Use /customerProductPatient/list/{applicationId}
        if (this.useMocks) {
            return this.mockRequest(mockVendorApplication);
        } else {
            // return this.http.post<ApiResponse<CustomerApplicationProduct[]>>(`${this.baseUrl}/customerProduct/getSearch`, params, this.authHeaders)
            return this.http.post<SingleResponse<CustomerApplicationProduct[]>>(`${this.baseUrl}/customerProduct/vendorSearch`, params, this.authHeaders)
                .pipe(map(response => {
                    // Response "result" is a string that needs to be parsed.
                    response.result = JSON.parse(response.result as any);
                    return response;
                }));
        }
    }

    public submitTaskRequest(taskType: TaskRequestType, payload: any) {
        if (this.useMocks) {
            return this.mockRequest<any>({ payload });
        } else {
            return this.http.post(`${this.baseUrl}/activityProcess/initializeTask`, { taskType, payload }, this.authHeaders);
        }
    }

    public submitApplication(application: any) {
        if (this.useMocks) {
            return this.mockRequest<any>({ referenceNumber: '123456' });
        } else {
            return this.http.post(`${this.baseUrl}/vendorApplication/submit`, application, this.authHeaders);
        }
    }

    public listBanks(): Observable<Bank[]> {
        if (this.useMocks) {
            return this.mockRequest(StaticBankList);
        }
        else {
            return this.http.get<Bank[]>(`${this.baseUrl}/bank/list`, this.authHeaders);
        }
    }

    public saveApplication(application: VendorApplication): Observable<VendorApplication> {
        if (this.useMocks) {
            return this.mockRequest<VendorApplication>(application);
        } else {
            return this.http.post<VendorApplication>(`${this.baseUrl}/vendorApplication/save`, application, this.authHeaders);
        }
    }

    public validateBankDetails(bankId: number, accountType: BankAccountType, accountNumber: string): Observable<BankAccountStatus> {
        const body: BankVerifyRequest = { bankId, accountType, accountNumber };

        if (this.useMocks) {
            const randomResult = Math.random() >= 0.5;
            return this.mockRequest<BankAccountStatus>(randomResult ? BankAccountStatus.Validated : BankAccountStatus.FailedInvalid);
        } else {
            return this.http.post<BankAccountStatus>(`${this.baseUrl}/bank/validate`, body, this.authHeaders);
        }
    }

    public calculateLoanAmount(vendorApplicationId: number): Observable<number> {
        if (this.useMocks) {
            return this.mockRequest<number>(12345);
        } else {
            return this.http.get<number>(`${this.baseUrl}/vendorApplication/calculateTotal/${vendorApplicationId}`, this.authHeaders);
        }
    }

    /**
     * USE WITH CAUTION - Deletes a patient from the database.
     * Used for:
     * - Delete Account Holder.
     * - Delete Patient from form.
     * @param patientId The patientID to delete.
     */
    public deletePatient(patientId: number) {
        if (this.useMocks) {
            return this.mockRequest<any>();
        } else {
            return this.http.get(`${this.baseUrl}/vendorApplicationCustomer/delete/${patientId}`, this.authHeaders);
        }
    }

    //#endregion
    //#region Document Emails and Downloads.

    public emailVendor(emailType: DocumentTypeEnum.StaffForm | DocumentTypeEnum.PayoutReport, vendorId: number): Observable<any> {
        if (this.useMocks) {
            return this.mockRequest<any>();
        }
        else {
            return this.http.post(`${this.baseUrl}/vendor/email`, {
                id: vendorId,
                documentType: emailType
            }, this.authHeaders);
        }
    }

    public emailVendorApplication(emailType: DocumentTypeEnum.LoanAgreement | DocumentTypeEnum.Quotation | DocumentTypeEnum.SigningDocuments, applicationId: number): Observable<any> {
        if (this.useMocks) {
            return this.mockRequest<any>();
        }
        else {
            return this.http.post(`${this.baseUrl}/vendorApplication/email`, {
                id: applicationId,
                documentType: emailType
            }, this.authHeaders);
        }
    }

    public emailVendorApplicationCustomer(emailType: DocumentTypeEnum.PatientAgreement, applicationId: number): Observable<any> {
        if (this.useMocks) {
            return this.mockRequest<any>();
        }
        else {
            return this.http.post(`${this.baseUrl}/vendorApplicationCustomer/email`, {
                id: applicationId,
                documentType: emailType
            }, this.authHeaders);
        }
    }

    public customerProductEmail(emailType: DocumentTypeEnum.Statement, productId: number): Observable<any> {
        if (this.useMocks) {
            return this.mockRequest<any>();
        }
        else {
            return this.http.post(`${this.baseUrl}/customerProduct/email`, {
                id: productId,
                documentType: emailType
            }, this.authHeaders);
        }
    }

    public vendorDocument(documentType: DocumentTypeEnum.StaffForm | DocumentTypeEnum.PayoutReport | DocumentTypeEnum.CustomerInformation,
             id: number, month?: Date, year?: Date, duration?: any): Observable<string> {
        if (this.useMocks) {
            return this.mockRequest<any>(this.createMockBase64ForDocumentType(documentType));
        }
        else {
            return this.http.post<string>(`${this.baseUrl}/vendor/document`, {
                id,
                documentType,
                month,
                year,
                duration
            }, this.authHeaders);
        }
    }

    public vendorApplicationDocument(documentType: DocumentTypeEnum.LoanAmendment | DocumentTypeEnum.LoanAgreement | DocumentTypeEnum.SigningDocuments | DocumentTypeEnum.Quotation, applicationId?: number, month?: Date, year?: Date, duration?: any): Observable<string> {
        if (this.useMocks) {
            return this.mockRequest<any>(this.createMockBase64ForDocumentType(documentType));
        }
        else {
            return this.http.post<string>(`${this.baseUrl}/vendorApplication/document`, {
                id: applicationId,
                documentType,
                month,
                year,
                duration
            }, this.authHeaders);
        }
    }

    public vendorApplicationCustomerDocument(documentType: DocumentTypeEnum.PatientAgreement, vendorId: number, month?: Date, year?: Date, duration?: any): Observable<string> {
        if (this.useMocks) {
            return this.mockRequest<any>(this.createMockBase64ForDocumentType(documentType));
        }
        else {
            return this.http.post<string>(`${this.baseUrl}/vendorApplicationCustomer/document`, {
                id: vendorId,
                documentType,
                month,
                year,
                duration
            }, this.authHeaders);
        }
    }

    public customerProductDocument(documentType: DocumentTypeEnum.Statement | DocumentTypeEnum.LoanAgreement, customerProductId: number, month?: Date, year?: Date, duration?: any): Observable<string> {
        if (this.useMocks) {
            return this.mockRequest<any>(this.createMockBase64ForDocumentType(documentType));
        }
        else {
            return this.http.post<string>(`${this.baseUrl}/customerProduct/document`, {
                id: customerProductId,
                documentType,
                month,
                year,
                duration
            }, this.authHeaders);
        }
    }

    //#endregion
    //#region Mock API Helpers

    public mockRequest<T>(data?: T, extendDelayMs?: number): Observable<T> {
        // Random delay to simulate network latency between 500ms and 2500ms
        const randomDelay = Math.floor(Math.random() * 2000) + 500 + (extendDelayMs || 0);
        return (of(data) as Observable<T>).pipe(delay(randomDelay));
    }

    /**
     * Creates an observable that emits the provided data.
     *
     * @template T The type of the data to emit.
     * @param {T} [data] The data to emit.
     * @return {Observable<T>} The observable with potential data.
     */
    protected mockObs<T>(data?: T, throwError = false): Observable<T> {
        return new Observable((observer) => {

            // Random delay to simulate API latency, between 500ms and 2500ms.
            const mockApiRuntime = Math.floor(Math.random() * 2000) + 500;

            setTimeout(() => {

                // Throw an error if requested.
                if (throwError) {
                    observer.error('An error occurred.');
                    return;
                }

                // Emit the data if it is provided.
                if (data) observer.next(data);
                // Otherwise emit nothing.
                else observer.next();

                // Complete the observable.
                observer.complete();

            }, mockApiRuntime);

        });
    }

    private createMockBase64ForDocumentType(documentType: DocumentTypeEnum): any {
        // All mocked PDF files are the same.
        // return this.sanitizer.bypassSecurityTrustUrl('data:application/pdf;base64,JVBERi0xLjIgDQo5IDAgb2JqDQo8PA0KPj4NCnN0cmVhbQ0KQlQvIDMyIFRmKCAgSGVsbG8gV29ybGQgICApJyBFVA0KZW5kc3RyZWFtDQplbmRvYmoNCjQgMCBvYmoNCjw8DQovVHlwZSAvUGFnZQ0KL1BhcmVudCA1IDAgUg0KL0NvbnRlbnRzIDkgMCBSDQo+Pg0KZW5kb2JqDQo1IDAgb2JqDQo8PA0KL0tpZHMgWzQgMCBSIF0NCi9Db3VudCAxDQovVHlwZSAvUGFnZXMNCi9NZWRpYUJveCBbIDAgMCAyNTAgNTAgXQ0KPj4NCmVuZG9iag0KMyAwIG9iag0KPDwNCi9QYWdlcyA1IDAgUg0KL1R5cGUgL0NhdGFsb2cNCj4+DQplbmRvYmoNCnRyYWlsZXINCjw8DQovUm9vdCAzIDAgUg0KPj4NCiUlRU9G');
        return 'data:application/pdf;base64,JVBERi0xLjIgDQo5IDAgb2JqDQo8PA0KPj4NCnN0cmVhbQ0KQlQvIDMyIFRmKCAgSGVsbG8gV29ybGQgICApJyBFVA0KZW5kc3RyZWFtDQplbmRvYmoNCjQgMCBvYmoNCjw8DQovVHlwZSAvUGFnZQ0KL1BhcmVudCA1IDAgUg0KL0NvbnRlbnRzIDkgMCBSDQo+Pg0KZW5kb2JqDQo1IDAgb2JqDQo8PA0KL0tpZHMgWzQgMCBSIF0NCi9Db3VudCAxDQovVHlwZSAvUGFnZXMNCi9NZWRpYUJveCBbIDAgMCAyNTAgNTAgXQ0KPj4NCmVuZG9iag0KMyAwIG9iag0KPDwNCi9QYWdlcyA1IDAgUg0KL1R5cGUgL0NhdGFsb2cNCj4+DQplbmRvYmoNCnRyYWlsZXINCjw8DQovUm9vdCAzIDAgUg0KPj4NCiUlRU9G';
    }

    //#endregion

}

type AngularLikeHttpHeaders = {
    headers?: HttpHeaders | {
        [header: string]: string | string[];
    };
};